import type {
  LiveChatVisitor,
  LiveChatVisitorList,
  LiveChatVisitorState,
} from '@nucleus-commerce/api-client/admin';

import type { ConversationEvent } from './frames';

export const VISITOR_STATES = [
  'chatting',
  'active',
  'idle',
] as const satisfies readonly LiveChatVisitorState[];

const ORDER: Record<LiveChatVisitorState, number> = { chatting: 0, active: 1, idle: 2 };

/** The API's order: chatting, active, idle; the newest visit first inside each group. */
export function compareVisitors(a: LiveChatVisitor, b: LiveChatVisitor): number {
  return (
    ORDER[a.state] - ORDER[b.state] ||
    (a.startedAt < b.startedAt ? 1 : a.startedAt > b.startedAt ? -1 : 0)
  );
}

export function countVisitors(
  items: readonly LiveChatVisitor[],
): Record<LiveChatVisitorState, number> {
  const counts = { chatting: 0, active: 0, idle: 0 };
  for (const item of items) counts[item.state] += 1;
  return counts;
}

function rebuild(items: LiveChatVisitor[]): LiveChatVisitorList {
  const sorted = items.sort(compareVisitors);
  return { items: sorted, counts: countVisitors(sorted) };
}

/** A `visitor.updated` row replaces the one held, or joins the list (a shopper who just arrived). */
export function upsertVisitor(
  list: LiveChatVisitorList | undefined,
  visitor: LiveChatVisitor,
): LiveChatVisitorList | undefined {
  if (!list) return list;
  return rebuild([...list.items.filter((item) => item.id !== visitor.id), visitor]);
}

/**
 * An inbox row frame carrying `visitorId`: an open or pending conversation makes the visitor
 * "chatting" at once; closing the one they were chatting in makes them active again until the
 * next refetch says otherwise.
 */
export function applyConversationToVisitors(
  list: LiveChatVisitorList | undefined,
  event: Pick<
    ConversationEvent,
    'id' | 'number' | 'status' | 'assignedTo' | 'visitorId' | 'trashed'
  >,
): LiveChatVisitorList | undefined {
  const visitorId = event.visitorId;
  if (!list || !visitorId) return list;
  const held = list.items.find((item) => item.id === visitorId);
  if (!held) return list;
  let next: LiveChatVisitor;
  if (event.status === 'closed' || event.trashed) {
    if (held.conversation?.id !== event.id) return list;
    next = { ...held, state: 'active', conversation: null };
  } else {
    next = {
      ...held,
      state: 'chatting',
      conversation: {
        id: event.id,
        number: event.number,
        status: event.status,
        assignedTo: event.assignedTo,
      },
    };
  }
  return rebuild([...list.items.filter((item) => item.id !== visitorId), next]);
}
